import { useState } from 'react';
import { formatMoney } from '@domain';
import type { Translate } from '../i18n/index.js';
import { theme } from '../theme.js';

/**
 * The three chart shapes the reports need, built out of plain HTML.
 *
 * WHY NOT SVG
 * Half this product is read right to left. An SVG has to be mirrored by hand —
 * every x, every text anchor, every label offset — and the first thing anybody
 * would forget is the one that makes a bar grow the wrong way. A div with a
 * width inside a container that inherits `direction` mirrors itself, correctly,
 * for free. Figures keep `dir="ltr"` so a minus sign cannot migrate.
 *
 * COLOUR
 * Every palette here comes from theme.ts, where each was run through the
 * data-viz validator rather than picked by eye. The rules that matter:
 * one hue light-to-dark for a ramp, two separable hues for a comparison, and
 * the firm's green/red only where position and a printed figure already say
 * the same thing.
 *
 * Text never wears the series colour. A mark beside a label carries identity;
 * the label itself stays in ink.
 */

/** Bars are thin, their ends rounded, and they sit on a common baseline. */
const BAR_RADIUS = '4px';

/**
 * A bar never fills its slot; the leftover is air.
 *
 * Two periods in a wide card made two slabs the size of doors, which stop
 * reading as a chart and start reading as a background. Twenty-four pixels
 * is the cap whatever the column count.
 */
const BAR_MAX = 24;

/**
 * Hover emphasis without changing a colour.
 *
 * Fading the other bars was the first attempt, and at 55% a solid green
 * reads as a second, paler green — which in a chart means a different
 * thing, not the same thing unfocused. An outline says "this one" and
 * leaves every hue exactly where it was.
 */
const hovered = (on: boolean) => (on ? { outline: `2px solid ${theme.nav}`, outlineOffset: '2px' } : {});

function Tip({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        position: 'absolute',
        bottom: 'calc(100% + 6px)',
        insetInlineStart: '50%',
        transform: 'translateX(-50%)',
        background: theme.nav,
        color: '#fff',
        borderRadius: '8px',
        padding: '5px 9px',
        fontSize: '11.5px',
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
        zIndex: 2,
      }}
    >
      {children}
    </div>
  );
}

/* ---------------------------------------------------------------------------
   Horizontal bars: magnitude across a handful of ordered categories.
--------------------------------------------------------------------------- */

export interface BarRow {
  label: string;
  value: number;
  /** Index into the ramp. Omitted rows share the darkest step. */
  step?: number;
}

/**
 * Ageing, and anything else that is a short ordered list of amounts.
 *
 * Horizontal because the categories have names worth reading — "Over 90 days"
 * will not fit under a column without being turned on its side, and a label
 * somebody has to tilt their head for is a label that does not get read.
 */
export function BarList({
  rows,
  currency,
  t,
  emptyLabel,
}: {
  rows: BarRow[];
  currency: string;
  t: Translate;
  emptyLabel?: string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(...rows.map((row) => Math.abs(row.value)), 0);

  if (max === 0) {
    return (
      <div style={{ padding: '14px 16px', fontSize: '12.5px', color: theme.textMuted }}>
        {emptyLabel ?? t('Nothing to show yet.')}
      </div>
    );
  }

  return (
    <div style={{ padding: '14px 16px', display: 'grid', gap: '9px' }}>
      {rows.map((row, index) => {
        const share = max === 0 ? 0 : Math.abs(row.value) / max;
        return (
          <div
            key={row.label}
            style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12.5px' }}
            onMouseEnter={() => setHover(index)}
            onMouseLeave={() => setHover(null)}
          >
            {/*
              The bar gets the room, not the furniture.

              Fixed-width label and figure columns look fine on a wide
              screen and leave the bar forty pixels across in a narrow
              pane — which is where this was first seen. The label and the
              figure now take only what they need, and the bar keeps a
              floor below which it stops being a chart.
            */}
            <div
              style={{
                flex: '0 1 auto',
                minWidth: 0,
                color: theme.label,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {row.label}
            </div>

            <div
              style={{
                flex: '1 1 90px',
                minWidth: '64px',
                position: 'relative',
                height: '18px',
                background: theme.surfaceSunken,
                borderRadius: BAR_RADIUS,
              }}
            >
              <div
                title={`${row.label}: ${formatMoney(row.value)} ${currency}`}
                style={{
                  width: `${Math.max(share * 100, row.value === 0 ? 0 : 1.5)}%`,
                  height: '100%',
                  borderRadius: BAR_RADIUS,
                  background:
                    theme.chartRamp[Math.min(row.step ?? index, theme.chartRamp.length - 1)],
                  ...hovered(hover === index),
                }}
              />
              {hover === index && (
                <Tip>
                  {row.label} · {formatMoney(row.value)} {currency}
                </Tip>
              )}
            </div>

            {/* Figures stay left to right so a minus sign cannot migrate. */}
            <div
              dir="ltr"
              style={{
                flex: '0 0 auto',
                textAlign: 'end',
                fontFamily: theme.fontMono,
                whiteSpace: 'nowrap',
                color: theme.text,
              }}
            >
              {formatMoney(row.value)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------------------------------------------------------------------------
   Columns over time, with a zero line.
--------------------------------------------------------------------------- */

export interface Column {
  label: string;
  value: number;
}

/**
 * A figure that can go either way, across periods.
 *
 * The green and red are the firm's, and they are the only place in this file
 * where a colour carries meaning on its own — so it is made not to. A profit
 * column grows up from the zero line and a loss grows down from it, and every
 * column prints its own figure. Somebody who cannot tell the two hues apart
 * loses nothing.
 */
export function ColumnChart({
  columns,
  currency,
  t,
}: {
  columns: Column[];
  currency: string;
  t: Translate;
}) {
  const [hover, setHover] = useState<number | null>(null);

  if (columns.length === 0) {
    return (
      <div style={{ padding: '14px 16px', fontSize: '12.5px', color: theme.textMuted }}>
        {t('Nothing to show yet.')}
      </div>
    );
  }

  const max = Math.max(...columns.map((c) => Math.abs(c.value)), 1);
  const anyNegative = columns.some((c) => c.value < 0);
  // Room below the line only when something actually goes below it, so a
  // company that never loses money does not stare at empty space.
  const plot = 150;
  const below = anyNegative ? 46 : 0;

  return (
    <div style={{ padding: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', height: `${plot + below}px` }}>
        {columns.map((column, index) => {
          const up = column.value >= 0;
          const height = Math.max((Math.abs(column.value) / max) * plot, 2);

          return (
            <div
              key={`${column.label}-${index}`}
              style={{
                flex: '1 1 0',
                minWidth: 0,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                position: 'relative',
              }}
              onMouseEnter={() => setHover(index)}
              onMouseLeave={() => setHover(null)}
            >
              {/* Above the line */}
              <div
                style={{
                  height: `${plot}px`,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                  position: 'relative',
                }}
              >
                {hover === index && (
                  <Tip>
                    {column.label} · {formatMoney(column.value)} {currency}
                  </Tip>
                )}
                {up && (
                  <div
                    title={`${column.label}: ${formatMoney(column.value)} ${currency}`}
                    style={{
                      height: `${height}px`,
                      // Capped and centred: the slot's leftover is air.
                      width: '100%',
                      maxWidth: `${BAR_MAX}px`,
                      marginInline: 'auto',
                      background: theme.chartGood,
                      // Rounded at the data end, square where it meets the
                      // baseline — a bar that is rounded at both ends looks
                      // as though it floats off the line it is measured from.
                      borderStartStartRadius: BAR_RADIUS,
                      borderStartEndRadius: BAR_RADIUS,
                      ...hovered(hover === index),
                    }}
                  />
                )}
              </div>

              <div style={{ height: '1px', background: theme.chartAxis }} />

              {/* Below the line */}
              {below > 0 && (
                <div style={{ height: `${below}px` }}>
                  {!up && (
                    <div
                      title={`${column.label}: ${formatMoney(column.value)} ${currency}`}
                      style={{
                        height: `${Math.min(height, below)}px`,
                        width: '100%',
                        maxWidth: `${BAR_MAX}px`,
                        marginInline: 'auto',
                        background: theme.chartBad,
                        borderEndStartRadius: BAR_RADIUS,
                        borderEndEndRadius: BAR_RADIUS,
                        ...hovered(hover === index),
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Direct labels, because four periods is few enough to label them all
          and a reader should not have to hover to know what they are seeing. */}
      <div style={{ display: 'flex', gap: '10px', marginTop: '7px' }}>
        {columns.map((column, index) => (
          <div
            key={`${column.label}-label-${index}`}
            style={{ flex: '1 1 0', minWidth: 0, textAlign: 'center' }}
          >
            <div
              dir="ltr"
              style={{
                fontFamily: theme.fontMono,
                fontSize: '11.5px',
                fontWeight: 600,
                color: theme.text,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {formatMoney(column.value)}
            </div>
            <div
              style={{
                fontSize: '11px',
                color: theme.textMuted,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {column.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
   Two series side by side.
--------------------------------------------------------------------------- */

export interface Group {
  label: string;
  values: [number, number];
}

/**
 * Two measures compared across a few categories — tax collected against tax
 * paid, by rate.
 *
 * A legend is always drawn, because two series cannot be told apart by
 * position alone and identity must never rest on colour by itself.
 */
export function GroupedBars({
  groups,
  seriesNames,
  currency,
  t,
}: {
  groups: Group[];
  seriesNames: [string, string];
  currency: string;
  t: Translate;
}) {
  const [hover, setHover] = useState<string | null>(null);
  const max = Math.max(...groups.flatMap((g) => g.values.map(Math.abs)), 0);

  if (groups.length === 0 || max === 0) {
    return (
      <div style={{ padding: '14px 16px', fontSize: '12.5px', color: theme.textMuted }}>
        {t('Nothing to show yet.')}
      </div>
    );
  }

  return (
    <div style={{ padding: '16px' }}>
      <div style={{ display: 'flex', gap: '18px', marginBottom: '14px', fontSize: '12px' }}>
        {seriesNames.map((name, i) => (
          <span key={name} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span
              style={{
                width: '11px',
                height: '11px',
                borderRadius: '3px',
                background: theme.chartSeries[i],
                display: 'inline-block',
              }}
            />
            <span style={{ color: theme.label, fontWeight: 600 }}>{name}</span>
          </span>
        ))}
      </div>

      <div style={{ display: 'grid', gap: '12px' }}>
        {groups.map((group) => (
          <div key={group.label}>
            <div style={{ fontSize: '12px', color: theme.label, fontWeight: 600, marginBottom: '5px' }}>
              {group.label}
            </div>
            <div style={{ display: 'grid', gap: '4px' }}>
              {group.values.map((value, i) => {
                const key = `${group.label}-${i}`;
                return (
                  <div
                    key={key}
                    style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
                    onMouseEnter={() => setHover(key)}
                    onMouseLeave={() => setHover(null)}
                  >
                    <div
                      style={{
                        flex: '1 1 90px',
                        minWidth: '64px',
                        position: 'relative',
                        height: '14px',
                        background: theme.surfaceSunken,
                        borderRadius: BAR_RADIUS,
                      }}
                    >
                      <div
                        title={`${group.label} · ${seriesNames[i]}: ${formatMoney(value)} ${currency}`}
                        style={{
                          width: `${Math.max((Math.abs(value) / max) * 100, value === 0 ? 0 : 1.5)}%`,
                          height: '100%',
                          borderRadius: BAR_RADIUS,
                          background: theme.chartSeries[i],
                          ...hovered(hover === key),
                        }}
                      />
                      {hover === key && (
                        <Tip>
                          {seriesNames[i]} · {formatMoney(value)} {currency}
                        </Tip>
                      )}
                    </div>
                    <div
                      dir="ltr"
                      style={{
                        flex: '0 0 auto',
                        whiteSpace: 'nowrap',
                        textAlign: 'end',
                        fontFamily: theme.fontMono,
                        fontSize: '12px',
                        color: value === 0 ? theme.textFaint : theme.text,
                      }}
                    >
                      {value === 0 ? '—' : formatMoney(value)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

